/**
 * Push notification helpers for Kin.
 *
 * Uses Median's OneSignal JavaScript bridge (window.median.onesignal). All
 * calls are guarded with optional chaining so the PWA / browser build does
 * not crash — these APIs only exist inside the Median native wrapper.
 *
 * This file owns registration and permission state only — binding the device
 * to a user (identifyOneSignalUser), reading/requesting permission, and the
 * soft-prompt flag. It does NOT schedule notification content.
 *
 * ⚠️ Client-side local scheduling (scheduleMorningSummary /
 * scheduleWeeklyNudgeIfNeeded) was removed here — Issue #836. It duplicated
 * dispatch-notifications' server-side morning/weekly send: both were keyed
 * off the same user_preferences time columns, so any user with the
 * notification legitimately turned on got buzzed twice — once by a
 * client-scheduled local push, once by the server at the actual moment —
 * with no shared id and nothing to de-dupe them. The server dispatcher is
 * the canonical path (retry policy, notification_dispatch_log audit trail,
 * SMS-channel awareness); the client-local path had none of that and was
 * inherited from the pre-migration Lovable app, not purpose-built alongside
 * the dispatcher. Removed rather than gated: a gate would only have stopped
 * a decline-still-fires leak that was never actually happening (every call
 * site already checked the preference first) — it would have done nothing
 * about the double-send, since both systems were correctly gated on the
 * same flag and would still both fire.
 */

import { supabase } from "@/integrations/supabase/client";

// ---------------------------------------------------------------------------
// Median bridge typing + availability
// ---------------------------------------------------------------------------

interface MedianOneSignal {
  requestPermission?: () => Promise<{ granted?: boolean } | boolean | void>;
  register?: () => void;
  login?: (externalId: string) => void | Promise<void>;
  onesignalInfo?: () => Promise<{ oneSignalSubscribed?: boolean } | undefined>;
  getPermissionStatus?: () => Promise<{ granted?: boolean }>;
  /** Only used by cancelLegacyLocalSchedules' one-time upgrade cleanup below. */
  cancelNotification?: (id: string) => Promise<void>;
}

// A device that already had a client-scheduled morning/weekly notification
// queued before #836 shipped would otherwise still fire that one remaining
// occurrence after upgrading, alongside the server dispatcher — the exact
// double-send this fix exists to kill, just delayed by up to a day/week
// instead of prevented outright. cancelLegacyLocalSchedules() below is a
// one-time cleanup for that (Copilot caught this on #842's review).
const LEGACY_MORNING_ID_KEY = "kin_notif_morning_id";
const LEGACY_MORNING_SCHEDULED_FOR_KEY = "kin_notif_morning_for";
const LEGACY_WEEKLY_ID_KEY = "kin_notif_weekly_id";
const LEGACY_WEEKLY_SCHEDULED_FOR_KEY = "kin_notif_weekly_for";

/** Bind the current OneSignal subscription to a stable external_id (the
 *  Supabase user.id) so the server-side dispatcher can target the user via
 *  `include_aliases: { external_id: [user_id] }`. Idempotent — safe to call
 *  repeatedly with the same id. No-op outside the Median wrapper. */
export function identifyOneSignalUser(userId: string): void {
  if (!isNotificationsAvailable()) return;
  if (!userId) return;
  try {
    const fn = window.median?.onesignal?.login;
    if (typeof fn === "function") {
      fn(userId);
      console.log("[OneSignalIdentify] login called for", userId);
    } else {
      console.warn("[OneSignalIdentify] median.onesignal.login not available");
    }
  } catch (err) {
    console.warn("[OneSignalIdentify] login threw", err);
  }
}

declare global {
  interface Window {
    median?: {
      onesignal?: MedianOneSignal;
    };
  }
}

/** True only when running inside the Median native wrapper with OneSignal
 *  exposed. The PWA / desktop browser will return false and all schedule
 *  helpers below become no-ops. */
export function isNotificationsAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return !!window.median?.onesignal;
}

/** Read current push permission status without prompting. Primary source is
 *  the Web Notifications API (a real standard, works in WKWebView for read).
 *  Median's getPermissionStatus is only consulted when the web standard says
 *  "default" or is unavailable, and we deliberately do NOT trust a Median
 *  {granted:false} as "denied" — we got burned trusting a similar speculative
 *  bridge method (postNotification). */
export async function getPushPermissionStatus(): Promise<"granted" | "denied" | "default"> {
  try {
    if (typeof Notification !== "undefined" && Notification.permission) {
      const p = Notification.permission;
      if (p === "granted" || p === "denied") return p;
      // p === "default" → fall through to Median check
    }
  } catch {
    // ignore — fall through
  }
  try {
    const fn = window.median?.onesignal?.onesignalInfo;
    if (typeof fn === "function") {
      const info: any = await fn();
      const sub = info?.subscription;
      if (sub && typeof sub === "object") {
        if (sub.optedIn === true) return "granted";
        if (sub.token) return "denied";
        return "default";
      }
      return "default";
    }
  } catch {
    // ignore
  }
  return "default";
}

/** Fire the iOS / Android push permission prompt. Returns true if granted.
 *  Called only when the user actively flips a notification toggle ON.
 *
 *  Per Median's official docs (Manual Registration setup), the correct method
 *  is `median.onesignal.register()`, NOT `requestPermission()`. register() is
 *  fire-and-forget and triggers the native iOS dialog without returning the
 *  granted/denied result. We then poll `onesignalInfo()` for the actual
 *  subscription state. See:
 *  https://docs.median.co/docs/push-registration-and-privacy-consent */
export async function requestPushPermission(): Promise<boolean> {
  const os: any = window.median?.onesignal;
  if (!os) return false;

  if (typeof os.register === "function") {
    try {
      os.register();
    } catch (err) {
      console.warn("median.onesignal.register failed:", err);
      return false;
    }
  } else if (typeof os.requestPermission === "function") {
    // Legacy fallback for older Median wrappers.
    try {
      await os.requestPermission();
    } catch {
      // ignore — we'll read status below
    }
  } else {
    return false;
  }

  // Fire-and-forget background poll for OneSignal subscription state — purely
  // informational for TestFlight logs. Does NOT gate the UI.
  void pollOneSignalSubscribed({ timeoutMs: 30000, intervalMs: 1000 })
    .then((subscribed) => {
      console.log("[notifications] background OneSignal subscribed =", subscribed);
    })
    .catch(() => {});

  // Poll for a definitive permission answer. APNs registration → subscription.optedIn
  // flip can take >500ms on first install; the previous fixed 500ms wait raced and
  // produced false "default" reads. Hard cap at 3000ms to prevent UI hang.
  let status: "granted" | "denied" | "default" = "default";
  const pollStart = Date.now();
  while (Date.now() - pollStart < 3000) {
    await new Promise((r) => setTimeout(r, 250));
    status = await getPushPermissionStatus();
    if (status === "granted" || status === "denied") break;
  }
  return status === "granted";
}

/** Polls median.onesignal.onesignalInfo() until oneSignalSubscribed flips to
 *  true, or the timeout elapses. Returns true if subscribed within timeout. */
async function pollOneSignalSubscribed(opts: {
  timeoutMs: number;
  intervalMs: number;
}): Promise<boolean> {
  const os: any = window.median?.onesignal;
  if (!os || typeof os.onesignalInfo !== "function") return false;

  const start = Date.now();
  while (Date.now() - start < opts.timeoutMs) {
    try {
      const info = await os.onesignalInfo();
      if (info && info.oneSignalSubscribed === true) return true;
    } catch {
      // ignore individual poll errors, keep trying
    }
    await new Promise((r) => setTimeout(r, opts.intervalMs));
  }
  return false;
}

/** Open the iOS app-specific Settings page so the user can flip Notifications
 *  back on after tapping "Don't Allow" on the one-shot iOS prompt. */
export function openIOSNotificationSettings(): void {
  try {
    window.location.href = "app-settings:";
  } catch (err) {
    console.warn("Could not open iOS settings:", err);
  }
}

/** Has the user already seen our soft prompt (in either Settings or the
 *  Today first-add trigger)? Reads notifications_prompted_at on
 *  user_preferences. Fail-safe: returns true on any error so we never
 *  re-pester a user we can't verify. */
export async function hasBeenSoftPrompted(userId: string): Promise<boolean> {
  if (!userId) return true;
  const { data, error } = await supabase
    .from("user_preferences")
    .select("notifications_prompted_at")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) return true;
  return !!(data as any)?.notifications_prompted_at;
}

/** Stamp notifications_prompted_at = now() so we never re-prompt this user. */
export async function markSoftPrompted(userId: string): Promise<void> {
  if (!userId) return;
  await supabase
    .from("user_preferences")
    .update({ notifications_prompted_at: new Date().toISOString() } as any)
    .eq("user_id", userId);
}

/** Read the current OneSignal subscription state without prompting. Used to
 *  short-circuit the soft prompt when the user already granted permission in
 *  a previous session. */
export async function isAlreadySubscribed(): Promise<boolean> {
  const os: any = window.median?.onesignal;
  if (!os || typeof os.onesignalInfo !== "function") return false;
  try {
    const info = await os.onesignalInfo();
    return !!(info && info.subscription && info.subscription.optedIn === true);
  } catch {
    return false;
  }
}

/**
 * Three-valued read of the device's own OneSignal subscription state, for the
 * "your notifications got switched off" card (#428).
 *
 * `isAlreadySubscribed()` above collapses every failure into `false`, which is
 * right for its job (short-circuiting a soft prompt — a spurious prompt costs
 * one extra tap). It is wrong for a card that TELLS her something is broken: an
 * unreadable bridge would accuse a healthy phone. So this keeps "couldn't read
 * it" as its own answer and lets the caller decide. See @/lib/pushReenable.
 */
export async function readPushSubscriptionState(): Promise<
  "subscribed" | "unsubscribed" | "unknown"
> {
  const os: any = window.median?.onesignal;
  if (!os || typeof os.onesignalInfo !== "function") return "unknown";
  try {
    const info = await os.onesignalInfo();
    if (!info || typeof info !== "object") return "unknown";
    const sub = info.subscription;
    if (sub && typeof sub === "object" && typeof sub.optedIn === "boolean") {
      return sub.optedIn ? "subscribed" : "unsubscribed";
    }
    // Older wrappers answer with a flat boolean instead of a subscription object.
    if (typeof info.oneSignalSubscribed === "boolean") {
      return info.oneSignalSubscribed ? "subscribed" : "unsubscribed";
    }
    // A shape we don't recognize is not a "no" — see the note above.
    return "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * One-time cleanup: cancels any morning/weekly notification a device
 * scheduled via the client-local path removed in #836, and clears the
 * localStorage keys that tracked it. A no-op on every call after the
 * first (the keys are gone), so it's safe to call unconditionally on every
 * authenticated mount rather than gating it behind a "have we cleaned up
 * yet" flag of its own.
 */
export async function cancelLegacyLocalSchedules(): Promise<void> {
  if (!isNotificationsAvailable()) return;
  for (const [idKey, forKey] of [
    [LEGACY_MORNING_ID_KEY, LEGACY_MORNING_SCHEDULED_FOR_KEY],
    [LEGACY_WEEKLY_ID_KEY, LEGACY_WEEKLY_SCHEDULED_FOR_KEY],
  ] as const) {
    try {
      const id = localStorage.getItem(idKey);
      if (id) {
        await window.median?.onesignal?.cancelNotification?.(id);
      }
    } catch (err) {
      console.warn("[notifications] legacy schedule cancel failed", err);
    } finally {
      localStorage.removeItem(idKey);
      localStorage.removeItem(forKey);
    }
  }
}

